import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Link,
  IconButton,
  InputAdornment,
  Divider
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  OpenInNew,
  Info,
  ContentCopy
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import OAUTH_FRONTEND_CONFIGS, { OAuthConnectionData } from '../../helpers/oauthConfig';

export interface OAuthDialogProps {
  open: boolean;
  provider: string;
  onClose: () => void;
  onConnect: (connectionData: OAuthConnectionData) => void;
}

const OAuthConnectionDialog: React.FC<OAuthDialogProps> = ({
  open,
  provider,
  onClose,
  onConnect
}) => {
  const theme = useTheme();
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [customParams, setCustomParams] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isConnecting, setIsConnecting] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const config = OAUTH_FRONTEND_CONFIGS[provider];
  const callbackUrl = `${window.location.origin}/auth/unified/callback`;

  // Reset form when dialog opens/closes
  React.useEffect(() => {
    if (open) {
      setClientId('');
      setClientSecret('');
      setCustomParams({});
      setErrors({});
      setIsConnecting(false);
      setShowClientSecret(false);
      setCopySuccess(false);
    }
  }, [open, provider]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate Client ID
    if (!clientId.trim()) {
      newErrors.clientId = 'Client ID is required';
    }

    // Validate Client Secret
    if (!clientSecret.trim()) {
      newErrors.clientSecret = 'Client Secret is required';
    }

    // Validate custom fields
    if (config?.customFields) {
      Object.entries(config.customFields).forEach(([fieldName, fieldConfig]) => {
        const value = customParams[fieldName] || '';
        
        if (fieldConfig.required && !value.trim()) {
          newErrors[fieldName] = `${fieldConfig.label} is required`;
        }
        
        if (value && fieldConfig.validation) {
          const { pattern, minLength, maxLength } = fieldConfig.validation;
          
          if (pattern && !new RegExp(pattern).test(value)) {
            newErrors[fieldName] = `${fieldConfig.label} format is invalid`;
          }
          
          if (minLength && value.length < minLength) {
            newErrors[fieldName] = `${fieldConfig.label} must be at least ${minLength} characters`;
          }
          
          if (maxLength && value.length > maxLength) {
            newErrors[fieldName] = `${fieldConfig.label} must be no more than ${maxLength} characters`;
          }
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConnect = async () => {
    if (!validateForm()) {
      return;
    }

    setIsConnecting(true);

    try {
      const connectionData: OAuthConnectionData = {
        provider,
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim(),
        customParams: { ...customParams }
      };

      await onConnect(connectionData);
    } catch (error) {
      console.error('Error connecting OAuth:', error);
      setErrors({ general: 'Failed to connect. Please check your credentials and try again.' });
    } finally {
      setIsConnecting(false);
    }
  };

  const handleCustomParamChange = (fieldName: string, value: string) => {
    setCustomParams(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Clear error for this field
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const handleCopyCallbackUrl = async () => {
    try {
      await navigator.clipboard.writeText(callbackUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy callback URL:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = callbackUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  if (!config) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Unsupported Provider</DialogTitle>
        <DialogContent>
          <Alert severity="error">
            OAuth provider "{provider}" is not supported.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' }
      }}
      sx={{
        zIndex: 9999 // Ensure dialog appears above other modals/drawers
      }}
    >
      <DialogTitle sx={{ pb: 3 }}>
        <Typography variant="h6" component="span">
          Connect {config.displayName}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* General Error */}
          {errors.general && (
            <Alert severity="error">{errors.general}</Alert>
          )}


          {/* OAuth Credentials Section */}
          <Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Copy Success Message - Always reserve space to prevent height jumping */}
              <Typography 
                variant="caption" 
                sx={{ 
                  color: copySuccess ? 'success.main' : 'transparent',
                  fontSize: '0.75rem',
                  mb: 1,
                  ml: 1.5,
                  mt: 1,
                  minHeight: '1.2em',
                  transition: 'color 0.2s ease-in-out'
                }}
              >
                {copySuccess ? '✓ Callback URL copied to clipboard' : '\u00A0'}
              </Typography>

              {/* Callback URL */}
              <TextField
                label="OAuth Callback URL"
                value={callbackUrl}
                fullWidth
                variant="outlined"
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleCopyCallbackUrl}
                        edge="end"
                        title="Copy callback URL"
                      >
                        <ContentCopy />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{ 
                  mt: 0,
                  '& .MuiInputBase-input': {
                    cursor: 'text',
                    userSelect: 'all'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'text.primary',
                    fontWeight: 500
                  }
                }}
              />
              
              {/* Callback URL Info Message */}
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  mt: -1,
                  ml: 1.5,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 0.5
                }}
              >
                <Info sx={{ fontSize: '0.875rem', mt: 0.125 }} />
                This callback URL needs to be added to your OAuth app settings
              </Typography>

              {/* Client ID */}
              <TextField
                label="Client ID"
                value={clientId}
                onChange={(e) => {
                  setClientId(e.target.value);
                  if (errors.clientId) {
                    setErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.clientId;
                      return newErrors;
                    });
                  }
                }}
                error={!!errors.clientId}
                helperText={errors.clientId}
                fullWidth
                required
                placeholder="Enter your OAuth Client ID"
                sx={{ mt: 1 }}
              />

              {/* Client Secret */}
              <TextField
                label="Client Secret"
                type={showClientSecret ? 'text' : 'password'}
                value={clientSecret}
                onChange={(e) => {
                  setClientSecret(e.target.value);
                  if (errors.clientSecret) {
                    setErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.clientSecret;
                      return newErrors;
                    });
                  }
                }}
                error={!!errors.clientSecret}
                helperText={errors.clientSecret}
                fullWidth
                required
                placeholder="Enter your OAuth Client Secret"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowClientSecret(!showClientSecret)}
                        edge="end"
                      >
                        {showClientSecret ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              {/* Setup Guide Link */}
              {config.setupGuide && (
                <Box sx={{ mt: 1 }}>
                  <Link
                    href={config.setupGuide}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem' }}
                  >
                    How to get Client ID and Client Secret <OpenInNew fontSize="small" />
                  </Link>
                </Box>
              )}
            </Box>
          </Box>

          {/* Provider-Specific Parameters */}
          {config.customFields && Object.keys(config.customFields).length > 0 && (
            <Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {Object.entries(config.customFields).map(([fieldName, fieldConfig]) => (
                    <Box key={fieldName}>
                      {fieldConfig.type === 'select' ? (
                        <FormControl fullWidth error={!!errors[fieldName]}>
                          <InputLabel required={fieldConfig.required}>
                            {fieldConfig.label}
                          </InputLabel>
                          <Select
                            value={customParams[fieldName] || ''}
                            onChange={(e) => handleCustomParamChange(fieldName, e.target.value)}
                            label={fieldConfig.label}
                            MenuProps={{
                              disablePortal: false,
                              sx: {
                                zIndex: 9999,
                              },
                              anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left',
                              },
                              transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left',
                              },
                              PaperProps: {
                                sx: {
                                  maxHeight: 300,
                                  marginTop: 1,
                                }
                              }
                            }}
                          >
                            {(() => {
                              if (!fieldConfig.options || fieldConfig.options.length === 0) {
                                return <MenuItem disabled>No options available</MenuItem>;
                              }
                              
                              return fieldConfig.options.map((option, index) => {
                                // Handle string options
                                if (typeof option === 'string') {
                                  return (
                                    <MenuItem key={`${option}-${index}`} value={option}>
                                      {option}
                                    </MenuItem>
                                  );
                                }
                                
                                // Handle object options with label/value
                                const optionObj = option as { label: string; value: string };
                                if (optionObj && optionObj.label && optionObj.value) {
                                  return (
                                    <MenuItem key={optionObj.value || `option-${index}`} value={optionObj.value}>
                                      {optionObj.label}
                                    </MenuItem>
                                  );
                                }
                                
                                // Fallback for invalid options
                                return (
                                  <MenuItem key={`invalid-${index}`} value="" disabled>
                                    Invalid option
                                  </MenuItem>
                                );
                              });
                            })()}
                          </Select>
                          {errors[fieldName] && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                              {errors[fieldName]}
                            </Typography>
                          )}
                        </FormControl>
                      ) : (
                        <TextField
                          label={fieldConfig.label}
                          value={customParams[fieldName] || ''}
                          onChange={(e) => handleCustomParamChange(fieldName, e.target.value)}
                          error={!!errors[fieldName]}
                          helperText={errors[fieldName] || fieldConfig.description}
                          fullWidth
                          required={fieldConfig.required}
                          placeholder={fieldConfig.placeholder}
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={onClose} 
          disabled={isConnecting}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConnect}
          variant="contained"
          disabled={isConnecting}
          color="primary"
        >
          {isConnecting ? 'Connecting...' : 'Connect'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OAuthConnectionDialog;
